import { useEffect, useMemo, useState } from 'react';

import { capitalize } from 'lodash-es';

import {
  IBackendCartEntries,
  ICartEntry,
  ICombo,
  IComboSlot,
  IItemOption,
  IPickerOption,
  MenuObject,
} from '@rbi-ctg/menu';
import { useMenuContext } from 'state/menu';
import { useUIContext } from 'state/ui';
import { CartEntryType, ComboSlotUiPattern } from 'utils/cart/types';
import { maybeMapCartEntryToMenuObjectIdentifier } from 'utils/menu';

interface UiPatternOption {
  _id?: string;
  uiPattern?: ComboSlotUiPattern | null;
}
interface UiPatternOptionMap {
  [id: string]: UiPatternOption;
}

/**
 * This hook takes a cart entry item and returns description
 * composed of comboSlot & itemOption selections.
 *
 * Return type can be either in the form of selection names,
 * or a single string containing comma separated selection names
 * depending on options specified.
 *
 * Note: includeModifiers also controls if we show comboSlot items.
 */
export function useComposeDescription(
  item: ICartEntry | IBackendCartEntries,
  options: {
    includeModifiers?: boolean;
    returnTypeArray: true;
    includeQuantity?: boolean;
  }
): string[];
export function useComposeDescription(
  item: ICartEntry | IBackendCartEntries,
  options?: {
    includeModifiers?: boolean;
    returnTypeArray?: false;
    includeQuantity?: boolean;
  }
): string;
export function useComposeDescription(
  item: ICartEntry | IBackendCartEntries,
  options: {
    includeModifiers?: boolean;
    returnTypeArray?: boolean;
    includeQuantity?: boolean;
  } = {}
): string | string[] {
  const { includeModifiers = false, returnTypeArray = false, includeQuantity = false } = options;
  const { formatCurrencyForLocale } = useUIContext();

  const { menuObject } = useCartEntryMenuObject({ cartEntry: item });

  const itemPricesToIgnore = getMenuObjectPricesToIgnore(menuObject);

  const uiPatternMap = getMenuObjectUiPatternMap(menuObject);

  const getDescription = (
    cartEntryChildren: Array<ICartEntry | IBackendCartEntries> = [],
    quantity: number,
    ignoredPricesSet: Set<string>
  ): string[] => {
    return cartEntryChildren.reduce<string[]>((desc, child) => {
      const uiPattern =
        ('uiPattern' in child ? child.uiPattern : null) || uiPatternMap[child.sanityId];
      if (
        (child.type === CartEntryType.comboSlot && uiPattern === ComboSlotUiPattern.HIDDEN) ||
        uiPattern === ComboSlotUiPattern.COLLAPSED
      ) {
        return desc;
      }
      if (
        child.type === CartEntryType.item ||
        (includeModifiers && child.type === CartEntryType.itemOptionModifier)
      ) {
        const itemName = (child.name || '').split(' ').map(capitalize).join(' ');
        const displayName = includeQuantity ? `${child.quantity || ''} ${itemName}` : itemName;

        const shouldIgnorePrice = ignoredPricesSet.has(child.sanityId);
        const price = shouldIgnorePrice ? 0 : (child.price || 0) * quantity;
        desc.push(
          price > 0 ? displayName.concat(` + ${formatCurrencyForLocale(price)}`) : displayName
        );
      }

      return desc.concat(getDescription(child.children, quantity, ignoredPricesSet));
    }, []);
  };

  const description = getDescription(item.children, item.quantity, itemPricesToIgnore);

  return returnTypeArray ? description : description.join(', ');
}

// TODO: FPF - Create separate files for this helpers

const useCartEntryMenuObject = ({ cartEntry }: { cartEntry: ICartEntry | IBackendCartEntries }) => {
  const { asyncGetMenuObject } = useMenuContext() || {};
  const [menuObject, setMenuObject] = useState<MenuObject | null>(null);

  const menuIdentifier = useMemo(() => maybeMapCartEntryToMenuObjectIdentifier(cartEntry), [
    cartEntry,
  ]);

  useEffect(() => {
    let mounted = true;

    // If a mapping cant be done, return cartEntry unmapped
    if (!menuIdentifier || !asyncGetMenuObject) {
      setMenuObject(null);
      return;
    }

    const getMenuObject = async () => {
      const { data } = await asyncGetMenuObject(menuIdentifier);
      if (data && mounted) {
        setMenuObject(data);
      }
    };

    getMenuObject();

    return () => {
      mounted = false;
    };
  }, [asyncGetMenuObject, menuIdentifier]);

  return { menuObject };
};

const getMenuObjectPricesToIgnore = (menuObject: MenuObject | null) => {
  const pricesToIgnore = new Set<string>();
  const mergedOptions = [
    ...(menuObject?.options || []),
    ...((menuObject as ICombo)?.mainItem?.options || []),
  ];
  mergedOptions.forEach((option: IComboSlot | IItemOption | MenuObject | IPickerOption) => {
    if ((option as IItemOption).allowMultipleSelections) {
      (option as IItemOption).options.forEach(opt => pricesToIgnore.add(opt._key));
    }
  });
  return pricesToIgnore;
};

const getMenuObjectUiPatternMap = (menuObject: MenuObject | null) => {
  const uiPatternMap = ((menuObject?.options as UiPatternOption[]) || []).reduce((acc, option) => {
    if (option._id && option.uiPattern) {
      acc[option._id] = option;
    }
    return acc;
  }, {} as UiPatternOptionMap);
  return uiPatternMap;
};
